import type { Ref } from 'vue'

import { useLocalStorage } from '@vueuse/core'
import { computed, ref, shallowRef, watch } from 'vue'

import { emitProxyError } from '../utils/proxy-error-emitter'

const PROXY_URL_KEY = 'settings/proxy/url'
const PROXY_ENABLED_KEY = 'settings/proxy/enabled'
const DEFAULT_PROXY_URL = import.meta.env.VITE_PROXY_SERVER_URL || 'https://luffy-ai-b89838124c1c.herokuapp.com'
// When VITE_PROXY_SERVER_URL is set, proxy is always enabled (server-managed mode)
const FORCE_PROXY_ENABLED = !!import.meta.env.VITE_PROXY_SERVER_URL

/**
 * Provider configuration from server (API keys masked)
 */
export interface ServerProviderConfig {
  enabled?: boolean
  apiKey?: string // Masked
  baseUrl?: string
  region?: string
  model?: string
  voice?: string
  accountId?: string
}

/**
 * Speech defaults from server
 */
export interface ServerSpeechDefaults {
  provider?: string
  model?: string
  voice?: string
  pitch?: number
  rate?: number
  ssmlEnabled?: boolean
}

/**
 * Server defaults configuration
 */
export interface ServerDefaults {
  chat?: {
    provider?: string
    model?: string
  }
  speech?: ServerSpeechDefaults
  transcription?: {
    provider?: string
    model?: string
  }
}

/**
 * Module configuration from server
 */
export interface ServerModules {
  consciousness?: {
    enabled?: boolean
  }
  speech?: {
    enabled?: boolean
  }
  hearing?: {
    enabled?: boolean
  }
  vision?: {
    enabled?: boolean
  }
  memory?: {
    shortTerm?: {
      enabled?: boolean
      maxMessages?: number
    }
    longTerm?: {
      enabled?: boolean
    }
  }
  gaming?: {
    minecraft?: {
      enabled?: boolean
      serverAddress?: string
      serverPort?: number
      username?: string
    }
    factorio?: {
      enabled?: boolean
      serverAddress?: string
      serverPort?: number
      username?: string
    }
  }
  messaging?: {
    discord?: {
      enabled?: boolean
      botToken?: string // Masked
      guildId?: string
      channelId?: string
    }
  }
}

/**
 * Character/Warrior of Liberation Card configuration
 */
export interface ServerCharacter {
  activeCardId?: string
  cards?: Array<{
    id?: string
    name?: string
    description?: string
    systemPrompt?: string
    personality?: string
  }>
}

/**
 * Full server configuration
 */
export interface ServerConfig {
  providers: {
    chat: Record<string, ServerProviderConfig>
    speech: Record<string, ServerProviderConfig>
    transcription: Record<string, ServerProviderConfig>
  }
  defaults?: ServerDefaults
  modules?: ServerModules
  character?: ServerCharacter
  enabled: {
    chat: string[]
    speech: string[]
    transcription: string[]
  }
  available: {
    chat: string[]
    speech: string[]
    transcription: string[]
  }
}

/**
 * Voice information from server
 */
export interface ServerVoice {
  id: string
  name: string
  previewUrl?: string
  languages?: Array<{ code: string, title: string }>
  labels?: Record<string, string>
  description?: string
}

/**
 * Model information from server
 */
export interface ServerModel {
  id: string
  name: string
  description?: string
}

// Singleton state shared across all instances
const serverConfig = shallowRef<ServerConfig | null>(null)
const configLoading = ref(false)
const configError = ref<string | null>(null)
const lastConfigFetch = ref<number>(0)

// Cache for voices and models
const voicesCache = shallowRef<Record<string, ServerVoice[]>>({})
const modelsCache = shallowRef<Record<string, ServerModel[]>>({})

const CONFIG_CACHE_TTL = 60_000 // 1 minute cache

/**
 * Composable for managing server-side configuration when proxy mode is enabled.
 * This replaces client-side localStorage configuration with server-provided config.
 */
export function useProxyConfig() {
  const proxyUrl = useLocalStorage<string>(PROXY_URL_KEY, DEFAULT_PROXY_URL)
  const proxyEnabled = useLocalStorage<boolean>(PROXY_ENABLED_KEY, true)

  function getProxyBaseUrl(): string {
    const url = proxyUrl.value || DEFAULT_PROXY_URL
    return url.endsWith('/') ? url.slice(0, -1) : url
  }

  function isProxyEnabled(): boolean {
    // When VITE_PROXY_SERVER_URL is set, always use proxy (server-managed mode)
    if (FORCE_PROXY_ENABLED) {
      return true
    }
    return proxyEnabled.value && !!getProxyBaseUrl()
  }

  const isConnected = computed(() => isProxyEnabled() && serverConfig.value !== null)

  /**
   * Fetch full configuration from server
   */
  async function fetchConfig(force = false): Promise<ServerConfig | null> {
    if (!isProxyEnabled()) {
      serverConfig.value = null
      return null
    }

    // Use cache if recent enough
    const now = Date.now()
    if (!force && serverConfig.value && (now - lastConfigFetch.value) < CONFIG_CACHE_TTL) {
      return serverConfig.value
    }

    configLoading.value = true
    configError.value = null

    try {
      const baseUrl = getProxyBaseUrl()
      const response = await fetch(`${baseUrl}/api/proxy/config`)

      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.statusText}`)
      }

      const config = await response.json() as ServerConfig
      serverConfig.value = config
      lastConfigFetch.value = now
      return config
    }
    catch (err) {
      configError.value = err instanceof Error ? err.message : 'Failed to fetch config'
      const error = err instanceof Error ? err : new Error(String(err))
      emitProxyError(error, 'fetchConfig')
      return null
    }
    finally {
      configLoading.value = false
    }
  }

  /**
   * Fetch voices for a speech provider from server
   */
  async function fetchVoices(providerId: string, force = false): Promise<ServerVoice[]> {
    if (!isProxyEnabled()) {
      return []
    }

    // Use cache if available
    if (!force && voicesCache.value[providerId]) {
      return voicesCache.value[providerId]
    }

    try {
      const baseUrl = getProxyBaseUrl()
      const response = await fetch(`${baseUrl}/api/proxy/voices/${providerId}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || 'Failed to fetch voices')
      }

      const data = await response.json()
      const voices = data.voices || []

      // Update cache
      voicesCache.value = {
        ...voicesCache.value,
        [providerId]: voices,
      }

      return voices
    }
    catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      emitProxyError(error, `fetchVoices:${providerId}`)
      return []
    }
  }

  /**
   * Fetch models for a provider from server
   */
  async function fetchModels(category: 'chat' | 'speech' | 'transcription', providerId: string, force = false): Promise<ServerModel[]> {
    if (!isProxyEnabled()) {
      return []
    }

    const cacheKey = `${category}:${providerId}`

    // Use cache if available
    if (!force && modelsCache.value[cacheKey]) {
      return modelsCache.value[cacheKey]
    }

    try {
      const baseUrl = getProxyBaseUrl()
      const response = await fetch(`${baseUrl}/api/proxy/models/${category}/${providerId}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || 'Failed to fetch models')
      }

      const data = await response.json()
      const models = data.models || []

      // Update cache
      modelsCache.value = {
        ...modelsCache.value,
        [cacheKey]: models,
      }

      return models
    }
    catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      emitProxyError(error, `fetchModels:${category}/${providerId}`)
      return []
    }
  }

  /**
   * Clear all cached data
   */
  function clearCache(): void {
    serverConfig.value = null
    voicesCache.value = {}
    modelsCache.value = {}
    lastConfigFetch.value = 0
  }

  // Auto-fetch config when proxy is enabled, clear when disabled
  watch(proxyEnabled, async (enabled) => {
    if (enabled && getProxyBaseUrl()) {
      // Auto-fetch config when proxy becomes enabled
      await fetchConfig()
    }
    else if (!enabled) {
      clearCache()
    }
  }, { immediate: true })

  // Computed getters for common config values
  const defaults = computed(() => serverConfig.value?.defaults)
  const modules = computed(() => serverConfig.value?.modules)
  const character = computed(() => serverConfig.value?.character)

  const enabledChatProviders = computed(() => serverConfig.value?.enabled?.chat || [])
  const enabledSpeechProviders = computed(() => serverConfig.value?.enabled?.speech || [])
  const enabledTranscriptionProviders = computed(() => serverConfig.value?.enabled?.transcription || [])

  // Default provider/model shortcuts
  const defaultChatProvider = computed(() => defaults.value?.chat?.provider)
  const defaultChatModel = computed(() => defaults.value?.chat?.model)
  const defaultSpeechProvider = computed(() => defaults.value?.speech?.provider)
  const defaultSpeechModel = computed(() => defaults.value?.speech?.model)
  const defaultSpeechVoice = computed(() => defaults.value?.speech?.voice)
  const defaultSpeechPitch = computed(() => defaults.value?.speech?.pitch ?? 0)
  const defaultSpeechRate = computed(() => defaults.value?.speech?.rate ?? 1)
  const defaultSpeechSsmlEnabled = computed(() => defaults.value?.speech?.ssmlEnabled ?? false)
  const defaultTranscriptionProvider = computed(() => defaults.value?.transcription?.provider)
  const defaultTranscriptionModel = computed(() => defaults.value?.transcription?.model)

  return {
    // Connection state
    proxyUrl,
    proxyEnabled,
    isProxyEnabled,
    getProxyBaseUrl,
    isConnected,

    // Config state
    serverConfig,
    configLoading,
    configError,

    // Fetchers
    fetchConfig,
    fetchVoices,
    fetchModels,
    clearCache,

    // Computed config accessors
    defaults,
    modules,
    character,
    enabledChatProviders,
    enabledSpeechProviders,
    enabledTranscriptionProviders,

    // Default shortcuts
    defaultChatProvider,
    defaultChatModel,
    defaultSpeechProvider,
    defaultSpeechModel,
    defaultSpeechVoice,
    defaultSpeechPitch,
    defaultSpeechRate,
    defaultSpeechSsmlEnabled,
    defaultTranscriptionProvider,
    defaultTranscriptionModel,

    // Voice/model cache access
    voicesCache,
    modelsCache,
  }
}

/**
 * Singleton instance for global access
 */
let _proxyConfigInstance: ReturnType<typeof useProxyConfig> | null = null

export function getProxyConfig(): ReturnType<typeof useProxyConfig> {
  if (!_proxyConfigInstance) {
    _proxyConfigInstance = useProxyConfig()
  }
  return _proxyConfigInstance
}
